import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { auth, db } from '../../firebase/firebaseConfig';
import { doc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { getDoc } from '../../utils/firestoreRead';
import './escaner.css';

function EstadoBadge({ estado }) {
  const e = (estado || '').toLowerCase();
  let cls = 'badge neutral';
  let label = estado || '—';
  if (e === 'usada') { cls = 'badge danger'; label = 'USADA'; }
  else if (e === 'no_usada') {
    cls = 'badge success'; label = 'VÁLIDA';
  } else if (e === 'anulada' || e === 'bloqueada') {
    cls = 'badge warn'; label = (estado || '').toUpperCase();
  }
  return <span className={cls}>{label}</span>;
}

export default function VistaInvitacionEscaner() {
  const { codigo } = useParams(); // ruta: /escanear/:codigo
  const navigate = useNavigate();

  const [checked, setChecked] = useState(false);
  const [inv, setInv] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [infoMsg, setInfoMsg] = useState('');

  // La ruta ya está protegida por <Protected roles=[owner/socio/organizador/porteria]>.
  // Antes esta pantalla volvía a leer usuarios/perfiles/custom claims antes de buscar la invitación.
  // En celulares eso provocaba esperas largas después del primer escaneo.
  useEffect(() => {
    let alive = true;

    async function cargarInvitacion() {
      setChecked(false);
      setError('');
      setInfoMsg('');

      try {
        if (!auth.currentUser) {
          setError('Sesión no disponible. Vuelve a iniciar sesión.');
          setInv(null);
          return;
        }

        const ref = doc(db, 'invitaciones', codigo);
        const snap = await getDoc(ref);

        if (!alive) return;
        setInv(snap.exists() ? { id: snap.id, ...snap.data() } : null);
      } catch (e) {
        console.error('Error cargando invitación escaneada:', e);
        if (!alive) return;
        setError('Error cargando la invitación. Revisa conexión o permisos.');
        setInv(null);
      } finally {
        if (alive) setChecked(true);
      }
    }

    cargarInvitacion();

    return () => { alive = false; };
  }, [codigo]);

  const marcarUsada = async () => {
    const estadoActual = (inv?.estado || '').toLowerCase();
    if (!inv || estadoActual === 'usada' || estadoActual === 'usado') return;

    setSaving(true);
    setError('');
    setInfoMsg('');

    try {
      const payload = {
        estado: 'usada',
        updatedAt: serverTimestamp(),
        usadaEn: serverTimestamp(),
        marcadaPor: auth.currentUser?.uid || null,
      };

      await updateDoc(doc(db, 'invitaciones', codigo), payload);

      setInv((p) => ({
        ...p,
        estado: 'usada',
        usadaEn: new Date(),
        marcadaPor: auth.currentUser?.uid || null,
      }));

      setInfoMsg('✅ Invitación marcada como USADA. Volviendo al escáner…');
      setTimeout(() => navigate('/escanear'), 1000);
    } catch (e) {
      console.error(e);
      try {
        await updateDoc(doc(db, 'invitaciones', codigo), {
          estado: 'usada',
          updatedAt: serverTimestamp(),
        });
        setInv((p) => ({ ...p, estado: 'usada' }));
        setInfoMsg('✅ Invitación marcada como USADA. Volviendo al escáner…');
        setTimeout(() => navigate('/escanear'), 1000);
      } catch (e2) {
        console.error(e2);
        setError('Permiso denegado o error al actualizar.');
      }
    } finally {
      setSaving(false);
    }
  };

  if (!checked) {
    return (
      <div className="scan-layout">
        <div className="scan-card">
          <div className="scan-title">Cargando invitación…</div>
          <p className="scan-msg">Validando el código escaneado.</p>
        </div>
      </div>
    );
  }

  const estado = inv?.estado || 'pendiente';
  const estadoLower = estado.toLowerCase();
  const yaUsada = estadoLower === 'usada' || estadoLower === 'usado';
  const cardState = !inv ? 'invalid' : yaUsada ? 'used' : 'valid';

  return (
    <div className="scan-layout">
      <header className="scan-header">
        <div className="brand-dot" /> <span>Palmarama · Invitación</span>
      </header>

      <section className="scan-result" style={{ maxWidth: 820, margin: '0 auto' }}>
        <div className={`result-card ${cardState}`}>
          <div className="result-status" style={{ marginBottom: 12 }}>
            {!inv ? (
              <>
                <div className="icon big">!</div>
                <div className="status-text">No encontrada</div>
              </>
            ) : yaUsada ? (
              <>
                <div className="icon big">✖</div>
                <div className="status-text">Invitación ya usada</div>
              </>
            ) : (
              <>
                <div className="icon big">✔</div>
                <div className="status-text">Invitación válida</div>
              </>
            )}
          </div>

          {error && <p className="scan-msg" style={{ color: '#ffb4b4' }}>{error}</p>}
          {infoMsg && <p className="scan-msg" style={{ color: '#c9ffe8' }}>{infoMsg}</p>}

          {inv && (
            <div className="result-body">
              <div className="row">
                <div className="label">Invitado</div>
                <div className="value big">{inv.nombre || '—'}</div>
              </div>

              <div className="row grid2">
                <div>
                  <div className="label">Promotor</div>
                  <div className="value">{inv.atribuidoANombre || '—'}</div>
                </div>
                <div>
                  <div className="label">Evento</div>
                  <div className="value">{inv.evento || '—'}</div>
                </div>
              </div>

              <div className="row">
                <div className="label">Estado</div>
                <div className="value"><EstadoBadge estado={estado} /></div>
              </div>
            </div>
          )}

          <div className="result-actions">
            {inv && !yaUsada && (
              <button className="btn primary" onClick={marcarUsada} disabled={saving}>
                {saving ? 'Marcando…' : 'Marcar como usada'}
              </button>
            )}
            <button className="btn ghost" onClick={() => navigate('/escanear')}>
              Volver al escáner
            </button>
          </div>
        </div>
      </section>
    </div>
  );
}
